import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useCopilotState } from "@/lib/copilot-state";
import { useAuth } from "@/lib/auth-context";
import { usePermissions } from "@/lib/permissions-context";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import {
  Sparkles, X, Send, RotateCcw, GripHorizontal, Loader2,
  CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  pendingConfirmation?: {
    actionId: string;
    toolName: string;
    toolInput: Record<string, any>;
    description: string;
  };
  confirmationStatus?: "pending" | "confirmed" | "cancelled";
}

export function CopilotHeaderButton() {
  const { toggle, isOpen } = useCopilotState();
  const { user } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const [glowing, setGlowing] = useState(false);

  // Hide until both auth and permissions are loaded; default-deny while loading
  // so a XATA replica failure or loading delay never leaks a blocked feature.
  const canUseAiAssistant = !!user && !permissionsLoading && hasPermission("General", "AI Assistant", user.role ?? "agent");

  useEffect(() => {
    if (isOpen) return;
    const interval = setInterval(() => {
      setGlowing(true);
      setTimeout(() => setGlowing(false), 2000);
    }, 30000);
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!canUseAiAssistant) return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className={`relative h-8 w-8 ${isOpen ? "text-primary" : ""}`}
      title="AI Assistant"
      data-testid="button-copilot-toggle"
    >
      <Sparkles className={`h-4 w-4 transition-all duration-500 ${glowing ? "text-primary scale-125 drop-shadow-[0_0_6px_rgba(16,107,198,0.7)]" : ""}`} />
      {glowing && (
        <span className="absolute inset-0 rounded-md animate-ping bg-primary/20 pointer-events-none" />
      )}
    </Button>
  );
}

export function FloatingCopilot() {
  const { isOpen, close } = useCopilotState();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [, setLocation] = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dragConstraintsRef = useRef<HTMLDivElement>(null);

  const { isLoading: permissionsLoading } = usePermissions();
  const canUseAiAssistant = !!user && !permissionsLoading && hasPermission("General", "AI Assistant", user.role ?? "agent");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const serverHistoryRef = useRef<Array<{ role: "user" | "assistant"; content: string }>>([]);

  const sendMutation = useMutation({
    mutationFn: async ({ message }: { message: string }) => {
      const res = await apiRequest("POST", "/api/copilot/message", { message, history: serverHistoryRef.current });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.history) serverHistoryRef.current = data.history;
      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.reply,
      };
      if (data.pendingConfirmation) {
        assistantMsg.pendingConfirmation = data.pendingConfirmation;
        assistantMsg.confirmationStatus = "pending";
      }
      setMessages(prev => [...prev, assistantMsg]);
      if (data.navigation) {
        setTimeout(() => setLocation(data.navigation), 600);
      }
    },
    onError: (err: any) => {
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: err.message || "Something went wrong. Please try again.",
      }]);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async ({ actionId, confirmed }: { actionId: string; confirmed: boolean }) => {
      const res = await apiRequest("POST", "/api/copilot/confirm", { actionId, confirmed });
      return res.json();
    },
    onSuccess: (data, variables) => {
      setMessages(prev => prev.map(m => {
        if (m.confirmationStatus === "pending" && m.pendingConfirmation?.actionId === variables.actionId) {
          return { ...m, confirmationStatus: variables.confirmed ? "confirmed" : "cancelled" };
        }
        return m;
      }));
      setMessages(prev => [...prev, {
        id: `confirm-result-${Date.now()}`,
        role: "assistant",
        content: data.reply,
      }]);
    },
    onError: (err: any, variables) => {
      setMessages(prev => prev.map(m => {
        if (m.confirmationStatus === "pending" && m.pendingConfirmation?.actionId === variables.actionId) {
          return { ...m, confirmationStatus: "cancelled" };
        }
        return m;
      }));
      setMessages(prev => [...prev, {
        id: `confirm-error-${Date.now()}`,
        role: "assistant",
        content: err.message || "Failed to execute the action. Please try again.",
      }]);
    },
  });

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sendMutation.isPending) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: trimmed,
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");

    sendMutation.mutate({ message: trimmed });
  };

  const handleReset = () => {
    setMessages([]);
    setInput("");
    serverHistoryRef.current = [];
  };

  const handleConfirm = (msg: ChatMessage, confirmed: boolean) => {
    if (!msg.pendingConfirmation) return;
    confirmMutation.mutate({
      actionId: msg.pendingConfirmation.actionId,
      confirmed,
    });
  };

  if (!canUseAiAssistant) return null;

  return (
    <>
      <div ref={dragConstraintsRef} className="fixed inset-0 pointer-events-none z-40" />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            drag
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-4 right-[400px] z-50"
            style={{ touchAction: "none" }}
          >
            <div
              className="w-[420px] h-[560px] rounded-lg shadow-2xl border bg-background flex flex-col overflow-hidden"
              data-testid="floating-copilot"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 cursor-grab active:cursor-grabbing select-none">
                <div className="flex items-center gap-2">
                  <GripHorizontal className="h-4 w-4 text-muted-foreground" />
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">AI Assistant</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleReset}
                    title="New Chat"
                    data-testid="button-copilot-reset"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={close}
                    data-testid="button-copilot-close"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <ScrollArea className="flex-1 overflow-hidden">
                <div className="p-3" style={{ width: "calc(420px - 2px)" }}>
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <Sparkles className="h-8 w-8 text-primary/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground mb-1">AI Assistant</p>
                    <p className="text-xs text-muted-foreground max-w-[280px]">
                      Ask me about call stats, queues, agents, contacts, voicemails, campaigns, CRM deals, schedules, or business hours. I can search the knowledge base, find canned responses, create tickets, update settings, navigate you to any page, and more.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div key={msg.id} data-testid={`copilot-message-${msg.id}`}>
                        <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[85%] min-w-0 ${msg.role === "user" ? "items-end" : "items-start"}`}>
                            <div
                              className={`px-3 py-2 rounded-lg text-sm break-all overflow-hidden ${
                                msg.role === "user"
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted"
                              }`}
                              style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                            >
                              {msg.content}
                            </div>
                          </div>
                        </div>
                        {msg.pendingConfirmation && msg.confirmationStatus === "pending" && (
                          <Card className="mt-2 p-3 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
                            <div className="flex items-start gap-2 mb-2">
                              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">Confirmation Required</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{msg.pendingConfirmation.description}</p>
                              </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => handleConfirm(msg, false)}
                                disabled={confirmMutation.isPending}
                                data-testid="button-copilot-cancel-action"
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleConfirm(msg, true)}
                                disabled={confirmMutation.isPending}
                                data-testid="button-copilot-confirm-action"
                              >
                                {confirmMutation.isPending ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                )}
                                Confirm
                              </Button>
                            </div>
                          </Card>
                        )}
                        {msg.confirmationStatus === "confirmed" && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-green-600 dark:text-green-400">
                            <CheckCircle2 className="h-3 w-3" /> Action confirmed
                          </div>
                        )}
                        {msg.confirmationStatus === "cancelled" && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <XCircle className="h-3 w-3" /> Action cancelled
                          </div>
                        )}
                      </div>
                    ))}
                    {sendMutation.isPending && (
                      <div className="flex justify-start">
                        <div className="bg-muted px-3 py-2 rounded-lg flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span className="text-xs text-muted-foreground">Thinking...</span>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
                </div>
              </ScrollArea>

              <div className="border-t p-2 flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask AI Assistant..."
                  className="text-sm h-8"
                  disabled={sendMutation.isPending}
                  data-testid="input-copilot-message"
                />
                <Button
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={handleSend}
                  disabled={!input.trim() || sendMutation.isPending}
                  data-testid="button-copilot-send"
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
